import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, BSModal, Field } from "../../components/helpers";

const EMPTY_FORM = {
  name: "",
  mobile: "",
  gender: "Male",
};

const MyPatients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // For the Add Patient Form
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMyPatients();
  }, []);

  const fetchMyPatients = async () => {
    setLoading(true);

    try {
      const doctor_id = localStorage.getItem("drId");

      const res = await apiFetch(
        process.env.REACT_APP_GET_PATIENT_LIST_FOR_DR,
        {
          method: "POST",
          body: {
            doctor_id: doctor_id,
          },
        },
      );

      if (res.status) {
        setPatients(res.data || []);
      } else {
        setPatients([]);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };
  const handleRowClick = (patientId) => {
    navigate(`/doctor/patients/${patientId}`);
  };

  const handleAddPatient = async () => {
    if (!form.name || !form.mobile)
      return alert("Please fill in the required fields");
    setSubmitting(true);
    try {
      // POST format using .env
      const res = await apiFetch(`${process.env.REACT_APP_REGISTER_PATIENT}`, {
        method: "POST",
        body: form,
      });
      if (res.status) {
        setModalOpen(false);
        setForm(EMPTY_FORM);
        fetchMyPatients();
      } else {
        alert(res.message || "Failed to add patient.");
      }
    } catch {
      alert("Error saving patient");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPatients = patients.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.mobile?.includes(searchTerm),
  );

  return (
    <div className="container-fluid p-3 p-lg-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1" style={{ color: "#0A1628" }}>
            My Patients
          </h2>
          <p className="text-muted mb-0">Browse and manage your patients.</p>
        </div>
        <button
          className="btn fw-bold text-white shadow-sm px-4"
          style={{ background: "#378ADD", borderRadius: "10px" }}
          onClick={() => setModalOpen(true)}
        >
          + Add New Patient
        </button>
      </div>

      {/* Search Form */}
      <div className="mb-4">
        <div
          className="card border-0 shadow-sm rounded-4 p-2 w-100"
          style={{ background: "#ffffff" }}
        >
          <div
            className="d-flex align-items-center bg-light rounded-3 px-3 py-1"
            style={{ border: "1px solid #E4EAF0" }}
          >
            <span style={{ fontSize: "18px", color: "#6B7A8D", opacity: 0.7 }}>
              🔍
            </span>
            <input
              type="text"
              className="form-control border-0 shadow-none bg-light fw-semibold"
              style={{
                padding: "12px 15px",
                fontSize: "14px",
                color: "#0A1628",
              }}
              placeholder="Search patients by name, mobile number or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-sm d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: 28,
                  height: 28,
                  background: "#E4EAF0",
                  color: "#6B7A8D",
                  border: "none",
                }}
                onClick={() => setSearchTerm("")}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 w-100">
        <div className="table-responsive">
          <table
            className="table table-hover align-middle mb-0"
            style={{ cursor: "pointer" }}
          >
            <thead style={{ background: "#f8f9fa", color: "#6c757d" }}>
              <tr>
                <th className="px-4 py-3 border-0 rounded-start-3">
                  Patient ID
                </th>
                <th className="px-4 py-3 border-0">Name</th>
                <th className="px-4 py-3 border-0">Mobile</th>
                <th className="px-4 py-3 border-0">Gender</th>
                <th className="px-4 py-3 border-0 rounded-end-3">Last Visit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredPatients.length > 0 ? (
                filteredPatients.map((patient) => (
                  <tr
                    key={patient.patient_id}
                    onClick={() => handleRowClick(patient.patient_id)}
                  >
                    <td className="px-4 py-3 fw-bold text-primary">
                      #{patient.patient_id}
                    </td>
                    <td
                      className="px-4 py-3 fw-bold"
                      style={{ color: "#0A1628" }}
                    >
                      {patient.name}
                    </td>
                    <td className="px-4 py-3 text-muted">{patient.mobile}</td>
                    <td className="px-4 py-3 text-muted">
                      {patient.gender || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="badge bg-soft-secondary text-secondary px-3 py-2 rounded-pill">
                        {patient.last_visit || "-"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    <div className="mb-3 fs-1 opacity-50">👥</div>
                    No patients found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attractive Add Patient Form Modal */}
      <BSModal
        open={modalOpen}
        title="Register New Patient"
        onClose={() => setModalOpen(false)}
        size="modal-md"
      >
        <div className="container-fluid p-0">
          <div className="row g-3 px-2 py-1">
            <div className="col-12">
              <Field label="Full Name *">
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Rahul Sharma"
                  style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
                />
              </Field>
            </div>

            <div className="col-6">
              <Field label="Mobile Number *">
                <input
                  className="form-control"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="+91..."
                  maxLength={10}
                  style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
                />
              </Field>
            </div>

            <div className="col-6">
              <Field label="Gender">
                <select
                  className="form-select"
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
                >
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </Field>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <button
              className="btn btn-light fw-semibold px-4"
              style={{ borderRadius: "8px" }}
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn fw-bold text-white px-4"
              style={{ background: "#378ADD", borderRadius: "8px" }}
              onClick={handleAddPatient}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Register Patient"}
            </button>
          </div>
        </div>
      </BSModal>
    </div>
  );
};

export default MyPatients;
